import React, { useEffect } from 'react';
import { Box, Typography, Grid, Avatar } from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  AccessTime as TimeIcon,
  Stadium as StadiumIcon,
  SportsSoccer as BallIcon,
} from '@mui/icons-material';
import { COLORS } from './MAMCStyles';

const FeaturedMatches = ({ matches, teams, scores, scorers }) => {
  // Filter for Semi Finals and Finals that are NOT finished (ongoing or upcoming)
  const featuredMatches = matches.filter(
    match => (match.type === 'Semi Final' || match.type === 'Final') && !match.isFinished
  );

  // Add glowing effect with CSS animations
  useEffect(() => {
    if (featuredMatches.length === 0) return;

    const styleElement = document.createElement('style');
    styleElement.textContent = `
      @keyframes finalGlow {
        0%, 100% { box-shadow: 0 0 30px rgba(218,165,32,0.6); }
        50% { box-shadow: 0 0 50px rgba(218,165,32,0.8); }
      }
      @keyframes semifinalGlow {
        0%, 100% { box-shadow: 0 0 20px rgba(218,165,32,0.4); }
        50% { box-shadow: 0 0 30px rgba(218,165,32,0.6); }
      }
      .final-spotlight {
        animation: finalGlow 3s infinite;
      }
      .semifinal-spotlight {
        animation: semifinalGlow 4s infinite;
      }
    `;
    document.head.appendChild(styleElement);

    return () => {
      if (styleElement.parentNode === document.head) {
        document.head.removeChild(styleElement);
      }
    };
  }, [featuredMatches.length]);

  // Return early if no featured matches
  if (featuredMatches.length === 0) return null;

  const getMatchDetails = match => {
    const homeTeam = teams.find(t => t.id === match.home_team_id);
    const awayTeam = teams.find(t => t.id === match.away_team_id);
    const score = scores.find(s => s.match_id === match.id);
    const matchScorers = scorers.filter(s => s.match_id === match.id);

    // Filter scorers by team
    const homeTeamScorers = matchScorers
      .filter(scorer => {
        const player = homeTeam?.players?.find(p => p.id === scorer.player_id);
        return !!player;
      })
      .sort((a, b) => a.score_minute - b.score_minute);

    const awayTeamScorers = matchScorers
      .filter(scorer => {
        const player = awayTeam?.players?.find(p => p.id === scorer.player_id);
        return !!player;
      })
      .sort((a, b) => a.score_minute - b.score_minute);

    return {
      homeTeam,
      awayTeam,
      score,
      matchScorers,
      homeTeamScorers,
      awayTeamScorers,
    };
  };

  const formatMatchDate = dateString => {
    if (!dateString) return 'Date TBA';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getSpotlightClass = matchType => {
    return matchType === 'Final' ? 'final-spotlight' : 'semifinal-spotlight';
  };

  return (
    <Box
      sx={{
        background: 'linear-gradient(145deg, rgba(0,0,0,0.95) 0%, rgba(26,26,26,0.98) 100%)',
        border: '1px solid rgba(218,165,32,0.4)',
        borderRadius: '24px',
        boxShadow: '0 20px 50px rgba(0,0,0,0.5), 0 0 30px rgba(218,165,32,0.2)',
        position: 'relative',
        overflow: 'hidden',
        p: { xs: 3, sm: 4, md: 5 },
        mb: { xs: 4, sm: 5, md: 6 },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '6px',
          background: 'linear-gradient(90deg, #B8860B, #DAA520, #FFD700, #DAA520, #B8860B)',
          borderRadius: '6px 6px 0 0',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background:
            'radial-gradient(circle at 50% 100%, rgba(218,165,32,0.15) 0%, transparent 50%)',
          zIndex: 0,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 4,
          position: 'relative',
        }}
      >
        <TrophyIcon
          sx={{
            color: COLORS.secondary,
            fontSize: { xs: 28, sm: 36, md: 46 },
            filter: 'drop-shadow(0 0 10px rgba(218,165,32,0.5))',
          }}
        />
        <Box>
          <Typography
            variant="h4"
            sx={{
              color: COLORS.text.accent,
              fontWeight: 'bold',
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
              textShadow: '0 2px 10px rgba(0,0,0,0.5)',
              letterSpacing: '2px',
              textTransform: 'uppercase',
            }}
          >
            Knockout Round
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              color: COLORS.text.secondary,
              fontSize: { xs: '0.8rem', sm: '1rem' },
              fontStyle: 'italic',
              mt: 0.5,
            }}
          >
            The ultimate battle for glory awaits!
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={4}>
        {featuredMatches.map(match => {
          const { homeTeam, awayTeam, score, homeTeamScorers, awayTeamScorers } =
            getMatchDetails(match);
          const isFinal = match.type === 'Final';
          const spotlightClass = getSpotlightClass(match.type);
          const ongoing = match.ongoing;
          const completed = match.isFinished;
          const statusColor = ongoing ? '#2196F3' : completed ? '#4CAF50' : '#FFA726';
          const statusText = ongoing ? 'LIVE NOW' : completed ? 'COMPLETED' : 'UPCOMING';

          return (
            <Grid item xs={12} key={match.id}>
              <Box
                className={spotlightClass}
                sx={{
                  borderRadius: '16px',
                  border: `2px solid ${isFinal ? '#FFD700' : 'rgba(218,165,32,0.5)'}`,
                  background:
                    'linear-gradient(to right, rgba(0,0,0,0.95), rgba(26,26,26,0.9), rgba(0,0,0,0.95))',
                  position: 'relative',
                  overflow: 'hidden',
                  py: { xs: 3, sm: 4 },
                  px: { xs: 2, sm: 3, md: 4 },
                  '&::before': isFinal
                    ? {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        background: 'url("/gallery/trophy_pattern.png")',
                        opacity: 0.05,
                        zIndex: 0,
                      }
                    : {},
                }}
              >
                {/* Match Type Badge */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    background: isFinal
                      ? 'linear-gradient(135deg, #B8860B, #FFD700)'
                      : 'linear-gradient(135deg, #B8860B, #DAA520)',
                    color: '#000',
                    fontWeight: 'bold',
                    px: 4,
                    py: 1,
                    clipPath: 'polygon(10% 0%, 100% 0%, 100% 100%, 0% 100%)',
                    fontSize: { xs: '0.8rem', sm: '1rem' },
                    zIndex: 5,
                  }}
                >
                  {match.type}
                </Box>

                {/* Match Status Indicator */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 16,
                    left: 16,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    zIndex: 5,
                  }}
                >
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      bgcolor: statusColor,
                      animation: ongoing ? 'pulse 1.5s infinite' : 'none',
                      '@keyframes pulse': {
                        '0%': { opacity: 0.6, transform: 'scale(1)' },
                        '50%': { opacity: 1, transform: 'scale(1.2)' },
                        '100%': { opacity: 0.6, transform: 'scale(1)' },
                      },
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: { xs: '0.7rem', sm: '0.8rem' },
                      color: statusColor,
                      fontWeight: 'bold',
                      letterSpacing: '1px',
                    }}
                  >
                    {statusText}
                  </Typography>
                </Box>

                {/* Teams and Score Section */}
                <Box sx={{ position: 'relative', zIndex: 2, mt: 2 }}>
                  <Grid container>
                    <Grid item xs={5} sx={{ textAlign: 'center' }}>
                      <Avatar
                        sx={{
                          width: { xs: 60, sm: 80, md: 100 },
                          height: { xs: 60, sm: 80, md: 100 },
                          bgcolor: 'rgba(218,165,32,0.2)',
                          fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                          border: '2px solid rgba(218,165,32,0.3)',
                          margin: '0 auto',
                          mb: 2,
                          fontWeight: 'bold',
                        }}
                      >
                        {homeTeam?.team_name.charAt(0) || '?'}
                      </Avatar>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 'bold',
                          fontSize: { xs: '1rem', sm: '1.5rem', md: '1.75rem' },
                          color: COLORS.text.primary,
                          mb: 1,
                        }}
                      >
                        {homeTeam?.team_name || 'TBD'}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: COLORS.text.secondary,
                          fontSize: { xs: '0.8rem', sm: '0.9rem' },
                        }}
                      >
                        {homeTeam?.group ? `Group ${homeTeam.group}` : ''}
                      </Typography>

                      {/* Home Team Scorers */}
                      {(ongoing || completed) && homeTeamScorers.length > 0 && (
                        <Box sx={{ mt: 2, textAlign: 'center' }}>
                          {homeTeamScorers.map((scorer, idx) => (
                            <Box
                              key={idx}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 0.5,
                                mb: 0.5,
                              }}
                            >
                              <BallIcon
                                sx={{
                                  color: COLORS.secondary,
                                  fontSize: { xs: 14, sm: 16 },
                                }}
                              />
                              <Typography
                                variant="caption"
                                sx={{
                                  color: COLORS.text.secondary,
                                  fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                }}
                              >
                                {scorer.player?.player_name || 'Unknown'} ({scorer.score_minute}')
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Grid>

                    <Grid
                      item
                      xs={2}
                      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      <Box
                        sx={{
                          bgcolor: 'rgba(0,0,0,0.7)',
                          px: { xs: 1.5, sm: 3 }, // Reduced padding on xs screens
                          py: { xs: 1, sm: 2 },
                          borderRadius: 2,
                          border: '1px solid rgba(255,255,255,0.1)',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          boxShadow: '0 0 20px rgba(0,0,0,0.3)',
                          position: 'relative',
                          minWidth: { xs: '70px', sm: '80px' }, // Ensure enough width on mobile
                          width: { xs: '100%', sm: 'auto' }, // Full width container on mobile
                          maxWidth: { xs: '90px', sm: '120px' }, // Limit maximum width
                        }}
                      >
                        {score ? (
                          <Box>
                            <Typography
                              sx={{
                                fontSize: { xs: '1.2rem', sm: '2rem', md: '2.5rem' }, // Smaller font on mobile
                                color: COLORS.text.accent,
                                fontWeight: 'bold',
                                letterSpacing: { xs: 0, sm: 1 }, // Remove letter spacing on mobile
                                lineHeight: 1,
                                whiteSpace: 'nowrap', // Keep on single line
                              }}
                            >
                              {score.home_team_score} - {score.away_team_score}
                            </Typography>
                            {ongoing && (
                              <Box
                                sx={{
                                  position: 'relative',
                                  '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    left: '50%',
                                    bottom: -8,
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    bgcolor: '#ff3d00',
                                    transform: 'translateX(-50%)',
                                    animation: 'blink 1s infinite',
                                  },
                                  '@keyframes blink': {
                                    '0%, 100%': { opacity: 1 },
                                    '50%': { opacity: 0.4 },
                                  },
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: '0.65rem',
                                    color: '#ff3d00',
                                    display: 'block',
                                    textAlign: 'center',
                                    fontWeight: 'bold',
                                    mt: 0.5,
                                  }}
                                >
                                  LIVE
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        ) : (
                          <Typography
                            sx={{
                              fontSize: { xs: '1rem', sm: '1.5rem', md: '1.8rem' }, // Smaller on mobile
                              color: COLORS.text.accent,
                              fontWeight: 'bold',
                            }}
                          >
                            VS
                          </Typography>
                        )}
                      </Box>
                    </Grid>

                    <Grid item xs={5} sx={{ textAlign: 'center' }}>
                      <Avatar
                        sx={{
                          width: { xs: 60, sm: 80, md: 100 },
                          height: { xs: 60, sm: 80, md: 100 },
                          bgcolor: 'rgba(218,165,32,0.2)',
                          fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                          border: '2px solid rgba(218,165,32,0.3)',
                          margin: '0 auto',
                          mb: 2,
                          fontWeight: 'bold',
                        }}
                      >
                        {awayTeam?.team_name.charAt(0) || '?'}
                      </Avatar>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 'bold',
                          fontSize: { xs: '1rem', sm: '1.5rem', md: '1.75rem' },
                          color: COLORS.text.primary,
                          mb: 1,
                        }}
                      >
                        {awayTeam?.team_name || 'TBD'}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: COLORS.text.secondary,
                          fontSize: { xs: '0.8rem', sm: '0.9rem' },
                        }}
                      >
                        {awayTeam?.group ? `Group ${awayTeam.group}` : ''}
                      </Typography>

                      {/* Away Team Scorers */}
                      {(ongoing || completed) && awayTeamScorers.length > 0 && (
                        <Box sx={{ mt: 2, textAlign: 'center' }}>
                          {awayTeamScorers.map((scorer, idx) => (
                            <Box
                              key={idx}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 0.5,
                                mb: 0.5,
                              }}
                            >
                              <BallIcon
                                sx={{
                                  color: COLORS.secondary,
                                  fontSize: { xs: 14, sm: 16 },
                                }}
                              />
                              <Typography
                                variant="caption"
                                sx={{
                                  color: COLORS.text.secondary,
                                  fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                }}
                              >
                                {scorer.player?.player_name || 'Unknown'} ({scorer.score_minute}')
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Grid>
                  </Grid>

                  {/* Match details */}
                  <Box
                    sx={{
                      mt: 4,
                      pt: 3,
                      borderTop: '1px solid rgba(218,165,32,0.2)',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: { xs: 2, sm: 4 },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TimeIcon sx={{ color: COLORS.text.accent }} />
                      <Typography sx={{ color: COLORS.text.primary }}>
                        {formatMatchDate(match.time)}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <StadiumIcon sx={{ color: COLORS.text.accent }} />
                      <Typography sx={{ color: COLORS.text.primary }}>
                        Field {match.field}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Exciting CTA */}
                  {!completed && (
                    <Box
                      sx={{
                        mt: 4,
                        textAlign: 'center',
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          color: COLORS.text.accent,
                          fontStyle: 'italic',
                          fontSize: { xs: '1rem', sm: '1.2rem', md: '1.4rem' },
                          textShadow: '0 2px 4px rgba(0,0,0,0.5)',
                        }}
                      >
                        {isFinal
                          ? "Don't miss the grand finale! Who will lift the trophy?"
                          : 'The road to the final! Who will advance to championship glory?'}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default FeaturedMatches;
